import { useEffect, useState } from "react";
import { AlertTriangle, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { defautTexte, fmtDateHeure } from "./common";

/** Bouton « État de la scan » : résumé + ouverture de l'éditeur. */
export function EtatScanButton({ defauts, onClick, testId = "btn-etat-scan" }) {
  const n = defauts?.length || 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-lg border p-3 transition-colors hover:bg-accent ${n ? "border-red-500/40 bg-red-500/5" : "border-border"}`}
      data-testid={testId}
    >
      <span className="flex items-center gap-2 text-sm font-semibold">
        {n ? <AlertTriangle size={15} className="text-red-500" /> : null}
        État de la scan {n ? `— ${n} élément${n > 1 ? "s" : ""} défectueux` : "— aucun défaut signalé"}
      </span>
      {n > 0 && <span className="block text-xs text-muted-foreground mt-1 line-clamp-2">{defauts.map(defautTexte).join(" · ")}</span>}
    </button>
  );
}

/**
 * Éditeur des éléments défectueux : catégorie (menu) -> état (boutons), ou
 * texte libre pour « Autre » ; « Ajouter » pour signaler un autre problème.
 * `list` / `setList` : liste contrôlée par le parent.
 */
export function DefautsPanel({ list, setList, categories }) {
  const [cat, setCat] = useState("");
  const [etat, setEtat] = useState("");
  const [detail, setDetail] = useState("");
  const catDef = categories.find((c) => c.id === cat);
  const canAdd = cat && (cat === "autre" ? detail.trim() : etat);

  const add = () => {
    if (!canAdd) return;
    setList((l) => [...l, { id: null, categorie: cat, categorie_label: catDef?.label, etat: cat === "autre" ? "" : etat, detail: detail.trim() }]);
    setCat(""); setEtat(""); setDetail("");
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {list.length === 0 && <p className="text-sm text-muted-foreground">Aucun élément défectueux.</p>}
        {list.map((d, i) => (
          <div key={d.id || `new-${i}`} className="flex items-start gap-2 rounded-lg border border-border p-2.5" data-testid="defaut-item">
            <AlertTriangle size={15} className="text-red-500 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{defautTexte(d)}</p>
              {d.signale_le
                ? <p className="text-[11px] text-muted-foreground">Signalé le {fmtDateHeure(d.signale_le)}{d.signale_par?.nom ? ` par ${d.signale_par.nom}` : ""}</p>
                : <p className="text-[11px] text-primary">Nouveau</p>}
            </div>
            <button type="button" onClick={() => setList((l) => l.filter((_, j) => j !== i))} className="p-1 rounded hover:bg-destructive/15 text-muted-foreground hover:text-destructive" aria-label="Retirer">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>

      <div className="rounded-lg bg-secondary/60 p-3 space-y-3">
        <div className="space-y-1.5">
          <Label>Élément</Label>
          <Select value={cat} onValueChange={(v) => { setCat(v); setEtat(""); }}>
            <SelectTrigger data-testid="defaut-categorie"><SelectValue placeholder="Choisir l'élément défectueux…" /></SelectTrigger>
            <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {catDef && cat !== "autre" && (
          <div className="space-y-1.5">
            <Label>État</Label>
            <div className="flex flex-wrap gap-1.5" data-testid="defaut-etats">
              {catDef.etats.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setEtat(e)}
                  className={`text-xs px-2.5 py-1.5 rounded-md border ${etat === e ? "border-red-500 bg-red-500/15 text-red-600 dark:text-red-400 font-semibold" : "border-border bg-card hover:bg-accent"}`}
                >
                  {e}
                </button>
              ))}
            </div>
            <Input value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Précision (facultatif)" className="h-8 text-sm" />
          </div>
        )}
        {cat === "autre" && (
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Input value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Décrivez le problème" autoFocus data-testid="defaut-autre" />
          </div>
        )}
        <Button type="button" size="sm" variant="outline" onClick={add} disabled={!canAdd} data-testid="defaut-ajouter">
          <Plus size={14} /> Ajouter
        </Button>
      </div>
    </div>
  );
}

/** Version fenêtre (Config scan → Éléments défectueux). */
export function DefautsEditor({ open, title = "Éléments défectueux", value, categories, onClose, onValidate, saving, readOnly }) {
  const [list, setList] = useState([]);
  useEffect(() => { if (open) setList(value || []); }, [open, value]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[92dvh] overflow-y-auto" data-testid="defauts-editor">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Éléments déclarés défectueux lors des attributions et réceptions.</DialogDescription>
        </DialogHeader>
        {readOnly ? (
          <div className="space-y-2">
            {!list.length && <p className="text-sm text-muted-foreground">Aucun élément défectueux.</p>}
            {list.map((d) => (
              <div key={d.id} className="rounded-lg border border-border p-2.5">
                <p className="text-sm font-medium">{defautTexte(d)}</p>
                <p className="text-[11px] text-muted-foreground">Signalé le {fmtDateHeure(d.signale_le)}{d.signale_par?.nom ? ` par ${d.signale_par.nom}` : ""}</p>
              </div>
            ))}
          </div>
        ) : (
          <DefautsPanel list={list} setList={setList} categories={categories} />
        )}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>{readOnly ? "Fermer" : "Annuler"}</Button>
          {!readOnly && <Button type="button" onClick={() => onValidate(list)} disabled={saving} data-testid="defauts-valider">Enregistrer</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Pour l'API : on ne renvoie que ce qui identifie un défaut. */
export function defautsPayload(list) {
  return (list || []).map((d) => ({ id: d.id || null, categorie: d.categorie, etat: d.etat || "", detail: d.detail || "" }));
}
